import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Injectable,
  PLATFORM_ID,
  WritableSignal,
  inject,
  signal,
} from '@angular/core';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { API_ENDPOINTS } from '@common/utils/api-endpoints.const';
import { ApiFundDetail } from '../../pages/home/fund-details/models/fund-detail-api.model';
import { FundsService } from '../../pages/home/fund-card/services/funds.service';
import {
  ApiCheckoutDetails,
  ApiCheckoutDetailsResponse,
} from '../../pages/investment/models/checkout-api.model';
import {
  ApiPaymentOption,
  ApiPaymentOptionsResponse,
} from '../../pages/investment/models/payment-options-api.model';
import {
  ApiSuitabilityAssessment,
  ApiSuitabilityResponse,
} from '../../pages/investment/models/suitability-assessment-api.model';

export interface PaymentDraft {
  units: number | null;
  paymentOptionId: string | null;
  termsAccepted: boolean;
}

/**
 * Result of the investment validation endpoint. `canInvest === false` is a
 * business validation (e.g. exceeds a limit) surfaced as a toast inside the
 * flow; `message` is the server-provided reason when present.
 */
export interface InvestmentValidationResult {
  canInvest?: boolean;
  message?: string;
}

/** Wraps cached data with the fund/offering it belongs to, so a restore can be rejected if stale. */
interface OfferingScoped<T> {
  fund: string;
  offering: string;
  data: T;
}

/** sessionStorage keys for the multi-step investment flow (survive refresh, not tabs). */
const LS = {
  FUND_DETAIL: 'inv_fund_detail',
  PAYMENT_OPTIONS: 'inv_payment_options',
  PAYMENT_DRAFT: 'inv_payment_draft',
  CHECKOUT_DETAILS: 'inv_checkout_details',
} as const;

/** Marks that the flow was just completed, so browser Back/Forward can't re-enter it. */
const COMPLETED_KEY = 'inv_completed';

/**
 * Central service for the multi-step investment flow.
 * Stores raw API responses directly — no mapping layer.
 * CSR only.
 */
@Injectable({ providedIn: 'root' })
export class InvestmentService {
  private readonly http = inject(HttpClient);
  private readonly fundsService = inject(FundsService);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  readonly fundId = signal('');
  readonly offeringId = signal('');
  readonly fundDetail = signal<ApiFundDetail | null>(null);
  readonly suitability = signal<ApiSuitabilityAssessment | null>(null);
  readonly checkoutDetails = signal<ApiCheckoutDetails | null>(null);
  readonly paymentOptions = signal<ApiPaymentOption[]>([]);
  readonly paymentDraft = signal<PaymentDraft | null>(null);
  readonly paymentFormValid = signal(false);
  readonly confirmationAccepted = signal(false);
  /** ISO timestamp captured when the user accepts the confirmation step — sent as acceptance_time. */
  readonly acceptanceTime = signal<string | null>(null);

  /** Registered by PaymentComponent; called by InvestmentPage on Continue. */
  private paymentValidationFn: (() => boolean) | null = null;

  /** Registered by InvestmentCheckoutComponent to show checkbox error on Continue. */
  private confirmationTouchedFn: (() => void) | null = null;

  /**
   * Registered by SuitabilityStepComponent to gate navigation off the step
   * (e.g. show the risk-mismatch modal). Receives the callback that performs
   * the actual navigation, which it must invoke itself once cleared to proceed.
   */
  private continueInterceptorFn: ((proceed: () => void) => void) | null = null;

  registerContinueInterceptor(fn: (proceed: () => void) => void): void {
    this.continueInterceptorFn = fn;
  }

  unregisterContinueInterceptor(): void {
    this.continueInterceptorFn = null;
  }

  /** Runs `proceed` directly, or hands it to the registered interceptor to decide when to run it. */
  runContinue(proceed: () => void): void {
    if (this.continueInterceptorFn) {
      this.continueInterceptorFn(proceed);
    } else {
      proceed();
    }
  }

  registerConfirmationTouched(fn: () => void): void {
    this.confirmationTouchedFn = fn;
  }
  unregisterConfirmationTouched(): void {
    this.confirmationTouchedFn = null;
  }
  touchConfirmation(): void {
    this.confirmationTouchedFn?.();
  }

  registerPaymentValidator(fn: () => boolean): void {
    this.paymentValidationFn = fn;
  }

  unregisterPaymentValidator(): void {
    this.paymentValidationFn = null;
  }

  /** Calls markAllAsTouched on the payment form and returns its validity. */
  validatePaymentForm(): boolean {
    return this.paymentValidationFn?.() ?? false;
  }

  savePaymentDraft(draft: PaymentDraft): void {
    this.paymentDraft.set(draft);
    this.saveToStorage<OfferingScoped<PaymentDraft>>(LS.PAYMENT_DRAFT, {
      fund: this.fundId(),
      offering: this.offeringId(),
      data: draft,
    });
  }

  storeFundDetail(fd: ApiFundDetail): void {
    this.fundDetail.set(fd);
    this.saveToStorage(LS.FUND_DETAIL, fd);
  }

  /**
   * Loads fund detail + payment options.
   * Suitability is intentionally excluded — call loadSuitability() after
   * validateEntry() returns canInvest=true.
   * Checkout details are also excluded — fetched at a later step.
   */
  initialize(
    fund: string,
    offering: string
  ): Observable<[ApiFundDetail, ApiPaymentOption[]]> {
    const cachedFundDetail = this.fundDetail();
    const fundDetail$: Observable<ApiFundDetail> = cachedFundDetail
      ? of(cachedFundDetail)
      : this.fundsService
          .getFundDetail(fund)
          .pipe(tap(fd => this.storeFundDetail(fd)));

    const paymentOptions$: Observable<ApiPaymentOption[]> =
      this.paymentOptions().length
        ? of(this.paymentOptions())
        : this.http
            .get<ApiPaymentOptionsResponse>(
              API_ENDPOINTS.PAYMENT_OPTIONS(fund, offering)
            )
            .pipe(
              map(res => {
                const arr: ApiPaymentOption[] = Array.isArray(
                  res?.paymentOptions
                )
                  ? res.paymentOptions
                  : [];
                return arr;
              }),
              tap(opts => {
                this.paymentOptions.set(opts);
                this.saveToStorage(LS.PAYMENT_OPTIONS, opts);
              }),
              catchError(() => {
                this.paymentOptions.set([]);
                return of([]);
              })
            );

    return forkJoin([fundDetail$, paymentOptions$]);
  }

  loadCheckoutDetails(
    fund: string,
    offering: string,
    force = false
  ): Observable<ApiCheckoutDetails> {
    if (!force) {
      const cached = this.checkoutDetails();
      if (cached) return of(cached);
    }
    const draft = this.paymentDraft();
    const body = {
      shares_count: draft?.units ?? 0,
      payment_option_id: Number(draft?.paymentOptionId ?? 0),
    };
    return this.http
      .post<ApiCheckoutDetailsResponse>(
        API_ENDPOINTS.INVESTMENT_CHECKOUT(fund, offering),
        body
      )
      .pipe(
        map(res => res.data),
        tap(f => {
          this.checkoutDetails.set(f);
          this.saveToStorage<OfferingScoped<ApiCheckoutDetails>>(
            LS.CHECKOUT_DETAILS,
            {
              fund,
              offering,
              data: f,
            }
          );
        })
      );
  }

  /** Fetches and caches the suitability assessment for a fund offering. */
  loadSuitability(
    fund: string,
    offering: string
  ): Observable<ApiSuitabilityAssessment> {
    const cached = this.suitability();
    if (cached) return of(cached);

    return this.http
      .get<ApiSuitabilityResponse>(
        API_ENDPOINTS.SUITABILITY_ASSESSMENT(fund, offering)
      )
      .pipe(
        map(res => (res?.data ?? res) as ApiSuitabilityAssessment),
        tap(s => this.suitability.set(s))
      );
  }

  /**
   * Full entry sequence for Investment Step 1:
   * validate first → only if canInvest, load fund detail + payment options
   * and the suitability assessment. When blocked, nothing else is fetched.
   * Returns the validation result so callers can react to canInvest=false.
   */
  enterFlow(
    fund: string,
    offering: string
  ): Observable<{ canInvest: boolean; retailInvestmentLimit?: number | null }> {
    // Restore state lost to a full page refresh (e.g. shares_count/payment_option_id
    // needed to re-call the checkout API, and the last checkout response itself).
    this.restoreScoped(fund, offering, LS.PAYMENT_DRAFT, this.paymentDraft);
    this.restoreScoped(
      fund,
      offering,
      LS.CHECKOUT_DETAILS,
      this.checkoutDetails
    );

    return this.validateEntry(fund, offering).pipe(
      switchMap(result => {
        if (!result.canInvest) return of(result);
        return forkJoin([
          this.initialize(fund, offering),
          this.loadSuitability(fund, offering),
        ]).pipe(map(() => result));
      })
    );
  }

  /**
   * Entry-time eligibility check (Invest Now from card/details).
   * Posts with no payload — the API decides eligibility from the session.
   */
  validateEntry(
    fund: string,
    offering: string
  ): Observable<{ canInvest: boolean; retailInvestmentLimit?: number | null }> {
    return this.http
      .post<{
        data?: { canInvest: boolean };
        canInvest?: boolean;
      }>(API_ENDPOINTS.VALIDATE_INVESTMENT(fund, offering), null)
      .pipe(
        map(
          res =>
            (res?.data ?? res) as {
              canInvest: boolean;
              retailInvestmentLimit?: number | null;
            }
        )
      );
  }

  submitInvestment(
    fund: string,
    offering: string,
    sharesCount: number,
    paymentOptionId: number
  ): Observable<InvestmentValidationResult> {
    return this.http
      .post<{
        data?: InvestmentValidationResult;
        canInvest?: boolean;
        message?: string;
      }>(API_ENDPOINTS.VALIDATE_INVESTMENT(fund, offering), {
        shares_count: sharesCount,
        payment_option_id: paymentOptionId,
      })
      .pipe(map(res => (res?.data ?? res ?? {}) as InvestmentValidationResult));
  }

  /** Submits the final investment request; acceptanceTime is the risk-acknowledgment timestamp. */
  createInvestment(
    fund: string,
    offering: string,
    sharesCount: number,
    paymentOptionId: number,
    acceptanceTime: string
  ): Observable<unknown> {
    return this.http.post(API_ENDPOINTS.INVESTMENTS(fund, offering), {
      shares_count: sharesCount,
      payment_option_id: paymentOptionId,
      acceptance_time: acceptanceTime,
    });
  }

  /** Marks the flow as just-completed so browser Back/Forward can't re-enter it. */
  markCompleted(): void {
    if (!this.isBrowser) return;
    sessionStorage.setItem(COMPLETED_KEY, '1');
  }

  /** True if the flow was completed and hasn't been freshly re-entered since. */
  wasCompleted(): boolean {
    return this.isBrowser && sessionStorage.getItem(COMPLETED_KEY) === '1';
  }

  /** Called on a genuine new entry into the flow to allow investing again. */
  clearCompletedFlag(): void {
    if (!this.isBrowser) return;
    sessionStorage.removeItem(COMPLETED_KEY);
  }

  resetFlow(): void {
    this.fundId.set('');
    this.offeringId.set('');
    this.fundDetail.set(null);
    this.suitability.set(null);
    this.checkoutDetails.set(null);
    this.paymentOptions.set([]);
    this.paymentDraft.set(null);
    this.paymentFormValid.set(false);
    this.confirmationAccepted.set(false);
    this.acceptanceTime.set(null);
    if (!this.isBrowser) return;
    Object.values(LS).forEach(key => sessionStorage.removeItem(key));
  }

  /**
   * One-time cleanup for stale flow state that earlier builds persisted in
   * localStorage (which outlived the session). Flow state now lives in
   * sessionStorage; call this once from the app shell to purge the leftovers.
   */
  clearLegacyStorage(): void {
    if (!this.isBrowser) return;
    Object.values(LS).forEach(key => localStorage.removeItem(key));
  }

  /** Restores a fund/offering-scoped value into `target` if the stored entry matches and nothing is set yet. */
  private restoreScoped<T>(
    fund: string,
    offering: string,
    key: string,
    target: WritableSignal<T | null>
  ): void {
    if (target()) return;
    const entry = this.loadFromStorage<OfferingScoped<T>>(key);
    if (entry && entry.fund === fund && entry.offering === offering) {
      target.set(entry.data);
    }
  }

  private saveToStorage<T>(key: string, value: T): void {
    if (!this.isBrowser) return;
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  private loadFromStorage<T>(key: string): T | null {
    if (!this.isBrowser) return null;
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  }
}
